import { useMemo, useState } from "react";
import api, { useList, DAYS, dayName, errMsg } from "@/lib/api";
import ResourcePage from "@/components/ResourcePage";
import { PageHeader, StatusBadge, EmptyState, Spinner } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const addMinutes = (hhmm, mins) => {
  const [h, m] = hhmm.split(":").map(Number);
  const t = h * 60 + m + mins;
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

function AssignDialog({ row, teachers, courses, studios, onDone, onClose }) {
  const [form, setForm] = useState({
    day_of_week: "", start_time: "", end_time: "",
    teacher_id: row.teacher_id || "", course_id: row.course_id || "", studio_id: row.studio_id || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === "start_time" && v && !f.end_time) next.end_time = addMinutes(v, 45);
    return next;
  });
  const submit = async () => {
    if (form.day_of_week === "" || !form.start_time || !form.end_time) {
      toast.error("Day, start and end time are required");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/schedules/${row.id}/assign`, {
        day_of_week: parseInt(form.day_of_week, 10),
        start_time: form.start_time, end_time: form.end_time,
        teacher_id: form.teacher_id || null, course_id: form.course_id || null,
        studio_id: form.studio_id || null,
      });
      toast.success(`${row.student_name} scheduled — enrolment is now active`);
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.conflicts ? d.conflicts.join("; ") : errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const Select = ({ k, label, options, placeholder }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <select className="w-full h-9 rounded-md border bg-background px-2 text-sm" value={form[k]}
        onChange={(e) => set(k, e.target.value)} data-testid={`assign-${k}`}>
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign day &amp; time — {row.student_name}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">{row.course_name || "No course linked"} · {row.teacher_name || "No teacher linked"}</p>
        <div className="grid grid-cols-2 gap-3">
          <Select k="day_of_week" label="Day" placeholder="Pick a day…"
            options={DAYS.map((d, i) => ({ value: i, label: d }))} />
          <Select k="studio_id" label="Studio (optional)" placeholder="No studio"
            options={studios.map((s) => ({ value: s.id, label: s.name }))} />
          <div>
            <Label className="text-xs">Start time</Label>
            <Input type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} data-testid="assign-start-time" />
          </div>
          <div>
            <Label className="text-xs">End time</Label>
            <Input type="time" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} data-testid="assign-end-time" />
          </div>
          <Select k="teacher_id" label="Teacher" placeholder="Pick a teacher…"
            options={teachers.map((t) => ({ value: t.id, label: t.full_name }))} />
          <Select k="course_id" label="Course" placeholder="Keep unlinked"
            options={courses.map((c) => ({ value: c.id, label: c.name }))} />
        </div>
        <Button onClick={submit} disabled={busy} data-testid="assign-submit-btn">
          {busy ? "Checking conflicts…" : "Assign & activate"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function NeedsSchedulingTab({ teachers, courses, studios }) {
  const { data: schedules, loading, reload } = useList("/schedules");
  const [search, setSearch] = useState("");
  const [assigning, setAssigning] = useState(null);
  const rows = useMemo(() => (schedules || [])
    .filter((s) => s.status === "needs_schedule")
    .filter((s) => !search || [s.student_name, s.teacher_name, s.course_name].join(" ")
      .toLowerCase().includes(search.toLowerCase())), [schedules, search]);
  if (loading) return <Spinner full />;
  return (
    <div data-testid="needs-scheduling-tab">
      <div className="flex items-center gap-3 mb-4">
        <Input placeholder="Search student / teacher / course…" value={search}
          onChange={(e) => setSearch(e.target.value)} className="max-w-sm" data-testid="needs-schedule-search" />
        <Badge variant="outline" data-testid="needs-schedule-count">{rows.length} awaiting a slot</Badge>
      </div>
      {rows.length === 0 ? <EmptyState text="Every enrolment has a day & time. Nice." /> : (
        <div className="rounded-lg border bg-card divide-y max-h-[60vh] overflow-auto">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`needs-row-${r.id}`}>
              <div className="min-w-0">
                <p className="font-medium truncate">{r.student_name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {r.course_name || "No course linked"} · {r.teacher_name || "No teacher"}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setAssigning(r)} data-testid={`assign-btn-${r.id}`}>
                Assign slot
              </Button>
            </div>
          ))}
        </div>
      )}
      {assigning && (
        <AssignDialog row={assigning} teachers={teachers} courses={courses} studios={studios}
          onDone={() => { setAssigning(null); reload(); }} onClose={() => setAssigning(null)} />
      )}
    </div>
  );
}

export function SchedulesPage() {
  const { data: students } = useList("/students");
  const { data: teachers } = useList("/teachers");
  const { data: courses } = useList("/courses");
  const { data: studios } = useList("/studios");

  return (
    <div data-testid="schedules-page-wrap">
      <PageHeader title="Class Schedules"
        description="Weekly recurring classes. The system blocks teacher, student and studio double-bookings automatically." />
      <Tabs defaultValue="all">
        <TabsList className="h-auto mb-4">
          <TabsTrigger value="all" data-testid="tab-all-schedules">All schedules</TabsTrigger>
          <TabsTrigger value="needs" data-testid="tab-needs-scheduling">Needs scheduling</TabsTrigger>
        </TabsList>
        <TabsContent value="all">
          <ResourcePage
            testId="schedules-page"
            title=""
            endpoint="/schedules"
            createLabel="New class schedule"
            searchKeys={["student_name", "teacher_name", "course_name", "studio_name"]}
            columns={[
              { key: "day_of_week", label: "Day", render: (r) => dayName(r.day_of_week) || "—" },
              { key: "start_time", label: "Time", render: (r) => <span className="font-mono text-xs">{r.start_time ? `${r.start_time}–${r.end_time}` : "—"}</span> },
              { key: "student_name", label: "Student", render: (r) => <span className="font-medium">{r.student_name}</span> },
              { key: "course_name", label: "Course" },
              { key: "teacher_name", label: "Teacher" },
              { key: "studio_name", label: "Studio" },
              { key: "status", label: "Status", badge: true },
            ]}
            fields={[
              { name: "student_id", label: "Student", type: "select", required: true,
                options: (students || []).filter((s) => s.status === "active").map((s) => ({ value: s.id, label: s.full_name })) },
              { name: "teacher_id", label: "Teacher", type: "select", required: true,
                options: (teachers || []).filter((t) => t.status === "active").map((t) => ({ value: t.id, label: t.full_name })) },
              { name: "course_id", label: "Course", type: "select", required: true,
                options: (courses || []).map((c) => ({ value: c.id, label: c.name })) },
              { name: "studio_id", label: "Studio", type: "select", required: true,
                options: (studios || []).map((s) => ({ value: s.id, label: s.name })) },
              { name: "day_of_week", label: "Day of week", type: "select", required: true, int: true,
                options: DAYS.map((d, i) => ({ value: i, label: d })) },
              { name: "start_time", label: "Start time", type: "time", required: true },
              { name: "end_time", label: "End time", type: "time", required: true },
              { name: "start_date", label: "Start date", type: "date" },
              { name: "end_date", label: "End date (optional)", type: "date" },
              { name: "status", label: "Status", type: "select", defaultValue: "active", options: [
                { value: "active", label: "Active" }, { value: "cancelled", label: "Cancelled" }] },
            ]}
            transform={(p) => ({ ...p, day_of_week: parseInt(p.day_of_week, 10), recurring_status: "weekly", status: p.status || "active" })}
            canDelete
          />
        </TabsContent>
        <TabsContent value="needs">
          <NeedsSchedulingTab teachers={teachers || []} courses={courses || []} studios={studios || []} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const TEACHER_COLORS = [
  "#0f766e", "#b45309", "#7c3aed", "#be123c", "#1d4ed8", "#15803d", "#c2410c",
  "#9333ea", "#0e7490", "#a16207", "#db2777", "#4338ca", "#65a30d", "#dc2626",
  "#0891b2", "#854d0e", "#6d28d9", "#047857",
];
const mins = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };

function WeeklyStudioGrid() {
  const { data: grid, loading } = useList("/studio-grid");
  const [day, setDay] = useState(0);
  const teacherColor = useMemo(() => {
    if (!grid) return {};
    const names = [...new Set(grid.blocks.map((b) => b.teacher_name))].sort();
    return Object.fromEntries(names.map((n, i) => [n, TEACHER_COLORS[i % TEACHER_COLORS.length]]));
  }, [grid]);
  if (loading) return <Spinner full />;
  if (!grid || grid.studios.length === 0) return <EmptyState text="No studios defined." />;
  const OPEN = 600, CLOSE = 1320, SPAN = CLOSE - OPEN; // 10:00–22:00
  const dayBlocks = grid.blocks.filter((b) => b.day_of_week === day);
  const hours = Array.from({ length: 13 }, (_, i) => 10 + i);
  const teachersToday = [...new Set(dayBlocks.map((b) => b.teacher_name))].sort();

  return (
    <div data-testid="weekly-studio-grid">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {DAYS.map((d, i) => (
          <Button key={d} size="sm" variant={day === i ? "default" : "outline"}
            onClick={() => setDay(i)} data-testid={`grid-day-${i}`}>{d.slice(0, 3)}</Button>
        ))}
        <span className="text-xs text-muted-foreground ml-2" data-testid="grid-day-block-count">
          {dayBlocks.length} blocks on {DAYS[day]}
        </span>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-card">
        <div className="flex min-w-[900px]">
          {/* time gutter */}
          <div className="w-16 shrink-0 border-r">
            <div className="h-14 border-b" />
            <div className="relative" style={{ height: 720 }}>
              {hours.map((h) => (
                <span key={h} className="absolute -translate-y-1/2 right-2 text-[10px] font-mono text-muted-foreground"
                  style={{ top: `${((h * 60 - OPEN) / SPAN) * 100}%` }}>
                  {h <= 12 ? `${h}${h === 12 ? "PM" : "AM"}` : `${h - 12}PM`}
                </span>
              ))}
            </div>
          </div>
          {grid.studios.map((s) => (
            <div key={s.id} className="flex-1 min-w-[120px] border-r last:border-r-0">
              <div className="h-14 border-b px-2 py-1.5">
                <p className="text-xs font-bold truncate">{s.name}</p>
                <p className="text-[10px] text-muted-foreground">{grid.utilization[s.id]}% wk util.</p>
              </div>
              <div className="relative" style={{ height: 720 }} data-testid={`grid-studio-col-${s.id}`}>
                {hours.map((h) => (
                  <div key={h} className="absolute left-0 right-0 border-t border-dashed border-border/60"
                    style={{ top: `${((h * 60 - OPEN) / SPAN) * 100}%` }} />
                ))}
                {dayBlocks.filter((b) => b.studio_id === s.id).map((b, i) => {
                  const top = ((mins(b.start_time) - OPEN) / SPAN) * 100;
                  const height = ((mins(b.end_time) - mins(b.start_time)) / SPAN) * 100;
                  return (
                    <div key={i} className="absolute left-0.5 right-0.5 rounded-sm px-1 py-0.5 overflow-hidden text-white"
                      style={{ top: `${top}%`, height: `${height}%`, backgroundColor: teacherColor[b.teacher_name] }}
                      title={`${b.start_time}–${b.end_time} · ${b.teacher_name}${b.label ? ` · ${b.label}` : ""}`}>
                      <p className="text-[10px] font-semibold leading-tight truncate">{b.teacher_name}</p>
                      <p className="text-[9px] leading-tight truncate opacity-80">{b.start_time}–{b.end_time}{b.label ? ` ${b.label}` : ""}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
        {teachersToday.map((n) => (
          <span key={n} className="inline-flex items-center gap-1.5 text-xs">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: teacherColor[n] }} />{n}
          </span>
        ))}
      </div>
    </div>
  );
}

export function BookingsPage({ defaultTab = "bookings" }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const { data: calendar, loading: calLoading } = useList(`/calendar?date=${date}`, [date]);
  const { data: studios } = useList("/studios");
  const { data: teachers } = useList("/teachers");
  const { data: students } = useList("/students");

  return (
    <div data-testid="bookings-page">
      <PageHeader title="Bookings & Calendar"
        description="One-off studio bookings (replacement / trial / ad-hoc) and the merged daily calendar." />
      <Tabs defaultValue={defaultTab}>
        <TabsList className="h-auto mb-6">
          <TabsTrigger value="grid" data-testid="tab-weekly-grid">Weekly studio grid</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">Daily calendar</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Studio bookings</TabsTrigger>
        </TabsList>

        <TabsContent value="grid">
          <WeeklyStudioGrid />
        </TabsContent>

        <TabsContent value="calendar">
          <div className="max-w-xs mb-4">
            <Input type="date" data-testid="calendar-date-input" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          {calLoading ? <Spinner full /> : !calendar || calendar.events.length === 0 ? (
            <EmptyState text="No classes or bookings on this date." />
          ) : (
            <div className="space-y-2 stagger-in" data-testid="calendar-events">
              {calendar.events.map((e, i) => (
                <div key={e.id || i} className="flex items-center gap-4 rounded-lg border bg-card px-4 py-3 lift">
                  <span className="font-mono text-xs w-28 shrink-0">{e.start_time}–{e.end_time}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {e.student_name || "—"} {e.course_name ? `· ${e.course_name}` : ""}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {e.teacher_name} · {e.studio_name}
                    </p>
                  </div>
                  <StatusBadge status={e.kind === "class" ? "active" : e.kind} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="bookings">
          <ResourcePage
            title=""
            endpoint="/bookings"
            createLabel="New booking"
            searchKeys={["student_name", "teacher_name", "studio_name", "booking_type"]}
            columns={[
              { key: "booking_date", label: "Date" },
              { key: "start_time", label: "Time", render: (r) => <span className="font-mono text-xs">{r.start_time}–{r.end_time}</span> },
              { key: "studio_name", label: "Studio" },
              { key: "booking_type", label: "Type", badge: false, render: (r) => <StatusBadge status={r.booking_type === "replacement" ? "pending" : "active"} />, },
              { key: "student_name", label: "Student" },
              { key: "teacher_name", label: "Teacher" },
              { key: "status", label: "Status", badge: true },
            ]}
            fields={[
              { name: "studio_id", label: "Studio", type: "select", required: true,
                options: (studios || []).map((s) => ({ value: s.id, label: s.name })) },
              { name: "booking_date", label: "Date", type: "date", required: true },
              { name: "start_time", label: "Start time", type: "time", required: true },
              { name: "end_time", label: "End time", type: "time", required: true },
              { name: "booking_type", label: "Type", type: "select", defaultValue: "adhoc", options: [
                { value: "adhoc", label: "Ad-hoc" }, { value: "trial", label: "Trial class" },
                { value: "replacement", label: "Replacement" }] },
              { name: "teacher_id", label: "Teacher (optional)", type: "select",
                options: (teachers || []).map((t) => ({ value: t.id, label: t.full_name })) },
              { name: "student_id", label: "Student (optional)", type: "select",
                options: (students || []).map((s) => ({ value: s.id, label: s.full_name })) },
              { name: "notes", label: "Notes", type: "textarea" },
            ]}
            canEdit={false}
            canDelete
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
